import type { DiaryEntry } from "@couples/shared";

import { supabase } from "../supabase";

interface EntryRow {
  id: string;
  couple_id: string;
  author_id: string;
  prompt_id: string | null;
  prompt_answer: string | null;
  body: string | null;
  created_at: string;
}

const COLUMNS = "id, couple_id, author_id, prompt_id, prompt_answer, body, created_at";

function toEntry(row: EntryRow): DiaryEntry {
  return {
    id: row.id,
    coupleId: row.couple_id,
    authorId: row.author_id,
    promptId: row.prompt_id,
    promptAnswer: row.prompt_answer,
    body: row.body,
    createdAt: row.created_at,
  };
}

/** 日記フィード。二人ぶんまとめて新しい順に引く */
export async function fetchEntries(): Promise<DiaryEntry[]> {
  const { data, error } = await supabase
    .from("diary_entries")
    .select(COLUMNS)
    .order("created_at", { ascending: false });
  if (error) throw error;
  const rows: EntryRow[] = data;
  return rows.map(toEntry);
}

export interface EntryInput {
  promptId: string | null;
  promptAnswer: string | null;
  body: string | null;
}

/** couple_id / author_id は DB 側の既定値でセッションから埋まる */
export async function createEntry(input: EntryInput): Promise<DiaryEntry> {
  const { data, error } = await supabase
    .from("diary_entries")
    .insert({ prompt_id: input.promptId, prompt_answer: input.promptAnswer, body: input.body })
    .select(COLUMNS)
    .single();
  if (error) throw error;
  return toEntry(data);
}

/** 消せるのは書いた本人だけ(RLS で担保) */
export async function deleteEntry(id: string): Promise<void> {
  const { error } = await supabase.from("diary_entries").delete().eq("id", id);
  if (error) throw error;
}

/** 書き換えられるのは書いた本人の、内容3列だけ(RLS + grant update で担保) */
export async function updateEntry(id: string, input: EntryInput): Promise<DiaryEntry> {
  const { data, error } = await supabase
    .from("diary_entries")
    .update({ prompt_id: input.promptId, prompt_answer: input.promptAnswer, body: input.body })
    .eq("id", id)
    .select(COLUMNS)
    .single();
  if (error) throw error;
  return toEntry(data);
}

interface LatestEntryRow {
  created_at: string;
}

/** 自分が最後に書いたエントリーの日時。1件も無ければ null(「今日ぶんを書いたか」の判定に使う) */
export async function fetchLatestOwnEntryAt(authorId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from("diary_entries")
    .select("created_at")
    .eq("author_id", authorId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  const row: LatestEntryRow | null = data;
  return row?.created_at ?? null;
}
